import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import {
  HiOutlineChevronDown, HiOutlineChevronRight, HiOutlinePlus,
  HiOutlineTrash, HiOutlineCheck, HiOutlinePencil, HiOutlineScale
} from 'react-icons/hi';
import AssigneePicker from './AssigneePicker';
import AttachmentList from './AttachmentList';
import { useAuth } from '../../../context/AuthContext';
import { hasCardAction } from '../../../utils/permissionHelper';

// Normalize {userId} entries that may be populated objects back to ids for PUT bodies
const normalizeAssigneeArr = (arr = []) =>
  arr.map(a => a.userId
    ? { userId: typeof a.userId === 'object' ? a.userId._id : a.userId }
    : { externalName: a.externalName }
  );

const STATUS = ['Pending', 'In Progress', 'Completed', 'Blocked'];
const statusBg = {
  Pending:        { bg: '#fef3c7', fg: '#92400e' },
  'In Progress':  { bg: '#dbeafe', fg: '#1e40af' },
  Completed:      { bg: '#d1fae5', fg: '#065f46' },
  Blocked:        { bg: '#fee2e2', fg: '#991b1b' },
  'Not Started':  { bg: '#f1f5f9', fg: '#475569' },
  'At Risk':      { bg: '#fee2e2', fg: '#991b1b' },
  'On Hold':      { bg: '#fef3c7', fg: '#92400e' }
};
const riskColor = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };
function pctColor(p) {
  if (p >= 80) return '#10b981';
  if (p >= 40) return '#a8336b';
  if (p >= 10) return '#f59e0b';
  return '#ef4444';
}

const ProgressBar = ({ pct }) => (
  <div style={{ background: '#f1f5f9', borderRadius: 99, height: 5, overflow: 'hidden', flex: 1 }}>
    <div style={{ width: `${pct || 0}%`, height: '100%', background: pctColor(pct), transition: 'width 0.3s' }} />
  </div>
);

const Pill = ({ children, bg = '#f1f5f9', fg = '#475569', style = {} }) => (
  <span style={{
    background: bg, color: fg, borderRadius: 999, fontSize: 10, fontWeight: 600,
    padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.3, ...style
  }}>{children}</span>
);

// ─── Inline edit: ActionItem status select ───────────────────────────────────
const StatusSelect = ({ value, onChange, options = STATUS }) => {
  const sb = statusBg[value] || { bg: '#f1f5f9', fg: '#475569' };
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      background: sb.bg, color: sb.fg, border: 'none', borderRadius: 999,
      fontSize: 10, fontWeight: 700, padding: '3px 8px', cursor: 'pointer',
      textTransform: 'uppercase', letterSpacing: 0.3
    }}>
      {options.map(s => <option key={s}>{s}</option>)}
    </select>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Subtask row
// ═══════════════════════════════════════════════════════════════════════════
const SubtaskRow = ({ taskId, sub, onChange, canEdit }) => {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(sub.title);

  const toggle = async () => {
    try {
      const { data } = await axiosInstance.patch(`/api/vision/tasks/${taskId}/subtasks/${sub._id}/toggle`);
      onChange(data.data);
    } catch (e) { console.error(e); }
  };
  const del = async () => {
    try {
      const { data } = await axiosInstance.delete(`/api/vision/tasks/${taskId}/subtasks/${sub._id}`);
      onChange(data.data);
    } catch (e) { console.error(e); }
  };
  const saveRename = async () => {
    const t = draft.trim();
    if (!t) { setRenaming(false); setDraft(sub.title); return; }
    if (t === sub.title) { setRenaming(false); return; }
    try {
      const { data } = await axiosInstance.patch(`/api/vision/tasks/${taskId}/subtasks/${sub._id}`, { title: t });
      onChange(data.data);
      setRenaming(false);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to rename subtask');
    }
  };
  const cancelRename = () => { setRenaming(false); setDraft(sub.title); };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
      <button onClick={toggle} style={{
        width: 14, height: 14, borderRadius: 3,
        border: '1.5px solid ' + (sub.isDone ? '#16a34a' : '#cbd5e1'),
        background: sub.isDone ? '#16a34a' : '#fff',
        cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {sub.isDone && <HiOutlineCheck size={9} color="#fff" />}
      </button>
      {renaming ? (
        <>
          <input
            value={draft} autoFocus
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') saveRename();
              else if (e.key === 'Escape') cancelRename();
            }}
            style={{ flex: 1, fontSize: 12, padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: 4 }}
          />
          <button onClick={saveRename} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '2px 6px', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>Save</button>
          <button onClick={cancelRename} style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '2px 6px', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>×</button>
        </>
      ) : (
        <>
          <span style={{
            flex: 1, color: sub.isDone ? '#94a3b8' : '#334155',
            textDecoration: sub.isDone ? 'line-through' : 'none'
          }}>{sub.title}</span>
          {canEdit && (
            <button onClick={() => setRenaming(true)} title="Rename subtask"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}>
              <HiOutlinePencil size={10} />
            </button>
          )}
          {canEdit && (
            <button onClick={del} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
              <HiOutlineTrash size={10} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Edit Task inline form
// ═══════════════════════════════════════════════════════════════════════════
const EditTaskInline = ({ task, systemUsers, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    deadline: task.deadline ? new Date(task.deadline).toISOString().slice(0, 10) : '',
    assignee: normalizeAssigneeArr(task.assignee)
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/vision/tasks/${task._id}`, {
        title: form.title.trim(),
        description: form.description,
        deadline: form.deadline || null,
        assignee: form.assignee
      });
      onSaved();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update task');
    } finally { setSaving(false); }
  };
  return (
    <div style={{ background: '#f8fafc', border: '1px dashed #94a3b8', borderRadius: 6, padding: 10, marginBottom: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Edit Task</div>
      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder="Task title…" autoFocus
        style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, boxSizing: 'border-box' }} />
      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="Description (optional)" rows={2}
        style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
        <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
          style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <AssigneePicker value={form.assignee} onChange={(arr) => setForm(f => ({ ...f, assignee: arr }))}
            systemUsers={systemUsers} compact placeholder="Assignees…" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Task node
// ═══════════════════════════════════════════════════════════════════════════
const TaskNode = ({ task, onUpdate, onDelete, onRefresh, onRollup, canEdit, systemUsers }) => {
  const [open, setOpen] = useState(false);
  const [newSub, setNewSub] = useState('');
  const [editing, setEditing] = useState(false);
  const subs = task.subtasks || [];
  const done = subs.filter(s => s.isDone).length;

  if (editing) {
    return (
      <EditTaskInline
        task={task}
        systemUsers={systemUsers}
        onSaved={() => { setEditing(false); onRefresh(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const handleStatus = async (status) => {
    if (status === 'Completed') {
      const pending = subs.filter(s => !s.isDone).length;
      if (pending > 0) {
        const ok = window.confirm(
          `Mark task as Completed?\n\nThis will also tick ${pending} pending subtask(s).`
        );
        if (!ok) return;
      }
    }
    try {
      const { data } = await axiosInstance.patch(`/api/vision/tasks/${task._id}/status`, { status });
      onRollup?.(data.rollup);
      onRefresh();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update status');
    }
  };
  const addSub = async () => {
    if (!newSub.trim()) return;
    try {
      const { data } = await axiosInstance.post(`/api/vision/tasks/${task._id}/subtasks`, { title: newSub.trim() });
      onUpdate(data.data);
      setNewSub('');
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8, boxShadow: '0 1px 2px rgba(15,23,42,.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setOpen(o => !o)} aria-label={`${open ? 'Collapse' : 'Expand'} task ${task.title}`} title={`${open ? 'Collapse' : 'Expand'} task`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 3, borderRadius: 4 }}>
          {open ? <HiOutlineChevronDown size={13} /> : <HiOutlineChevronRight size={13} />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#64748b', letterSpacing: '.08em', marginBottom: 2 }}>TASK</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{task.title}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center', fontSize: 10, color: '#94a3b8', flexWrap: 'wrap' }}>
            {task.deadline && <span>📅 due {new Date(task.deadline).toLocaleDateString('en-GB')}</span>}
            {task.completedAt
              ? <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ done {new Date(task.completedAt).toLocaleDateString('en-GB')}</span>
              : task.updatedAt && <span>🕒 updated {new Date(task.updatedAt).toLocaleDateString('en-GB')}</span>}
            {(task.assignee || []).length > 0 && (
              <span>👤 {(task.assignee || []).map(a => a.userId?.fullName || a.externalName).join(', ')}</span>
            )}
            {subs.length > 0 && <span>☑ {done}/{subs.length}</span>}
            <Pill>{task.weight}%</Pill>
            {task.autoRisk && task.autoRisk !== 'Low' && <Pill bg="#fee2e2" fg="#991b1b">⚠ {task.autoRisk}</Pill>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <ProgressBar pct={task.completionPercentage} />
            <span style={{ fontSize: 10, color: '#64748b', fontWeight: 700, minWidth: 28, textAlign: 'right' }}>
              {task.completionPercentage}%
            </span>
          </div>
        </div>
        <StatusSelect value={task.status} onChange={handleStatus} />
        {canEdit && (
          <button onClick={() => setEditing(true)} title="Edit task"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}>
            <HiOutlinePencil size={11} />
          </button>
        )}
        {canEdit && (
          <button onClick={() => onDelete(task._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
            <HiOutlineTrash size={11} />
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 9, paddingLeft: 20, borderTop: '1px dashed #cbd5e1', paddingTop: 8 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#64748b', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>Subtasks ({subs.length})</div>
          {subs.map(s => <SubtaskRow key={s._id} taskId={task._id} sub={s} onChange={onUpdate} canEdit={canEdit} />)}
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <input value={newSub} onChange={e => setNewSub(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSub()}
              placeholder="+ subtask…"
              style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
            <button onClick={addSub} className="v26-btn v26-btn--secondary v26-btn--sm" style={{ fontSize: 10 }}>Add</button>
          </div>
          <AttachmentList
            entityType="tasks"
            entityId={task._id}
            attachments={task.attachments || []}
            canEdit={canEdit}
            onChange={() => onRefresh()}
            compact
          />
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Add Task form (inline under an Action Item)
// ═══════════════════════════════════════════════════════════════════════════
const AddTaskInline = ({ actionItemId, systemUsers, onCreated, onCancel }) => {
  const [form, setForm] = useState({ title: '', deadline: '', assignee: [] });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.post('/api/vision/tasks', {
        actionItem: actionItemId, title: form.title.trim(),
        deadline: form.deadline || undefined, assignee: form.assignee
      });
      onCreated(data.data);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to add task');
    } finally { setSaving(false); }
  };
  return (
    <div className="v26-roadmap-form" style={{ background: '#fff', borderRadius: 8, padding: 12, marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', border: '1px solid #e2e8f0' }}>
      <label className="v26-roadmap-field" style={{ flex: 2, minWidth: 150 }}><span>Task name</span><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder="e.g. Finalize product specifications" autoFocus /></label>
      <label className="v26-roadmap-field" style={{ flex: '1 1 125px' }}><span>Due date</span><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} /></label>
      <div style={{ flex: 2, minWidth: 180 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 5 }}>Assignees</div>
        <AssigneePicker value={form.assignee} onChange={(arr) => setForm(f => ({ ...f, assignee: arr }))}
          systemUsers={systemUsers} compact placeholder="Select assignees…" />
      </div>
      <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
        {saving ? 'Adding…' : 'Add task'}
      </button>
      <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm" aria-label="Cancel adding task">Cancel</button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Edit Action Item inline form
// ═══════════════════════════════════════════════════════════════════════════
const EditActionItemInline = ({ ai, systemUsers, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    title: ai.title || '',
    description: ai.description || '',
    deadline: ai.deadline ? new Date(ai.deadline).toISOString().slice(0, 10) : '',
    priority: ai.priority || 'Medium',
    responsible: normalizeAssigneeArr(ai.responsible)
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/vision/action-items/${ai._id}`, {
        title: form.title.trim(),
        description: form.description,
        deadline: form.deadline || null,
        priority: form.priority,
        responsible: form.responsible
      });
      onSaved();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update action item');
    } finally { setSaving(false); }
  };
  return (
    <div style={{ background: '#faf5ff', border: '1px dashed #c084fc', borderRadius: 8, padding: 12, marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Edit Action Item</div>
      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder="Action item title…" autoFocus
        style={{ width: '100%', fontSize: 13, padding: '5px 9px', border: '1px solid #ddd6fe', borderRadius: 4, marginBottom: 6, boxSizing: 'border-box' }} />
      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="Description (optional)" rows={2}
        style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd6fe', borderRadius: 4, marginBottom: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
        <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
          style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd6fe', borderRadius: 4 }} />
        <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
          style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd6fe', borderRadius: 4 }}>
          {['Low','Medium','High','Urgent'].map(p => <option key={p}>{p}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 8 }}>
        <AssigneePicker value={form.responsible} onChange={(arr) => setForm(f => ({ ...f, responsible: arr }))}
          systemUsers={systemUsers} compact placeholder="Responsible…" />
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  ActionItem node
// ═══════════════════════════════════════════════════════════════════════════
const ActionItemNode = ({ ai, systemUsers, onRefresh, onRollup, breadcrumb, canEdit }) => {
  const [open, setOpen] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tasks, setTasks] = useState(ai.tasks || []);

  useEffect(() => { setTasks(ai.tasks || []); }, [ai.tasks]);

  if (editing) {
    return (
      <EditActionItemInline
        ai={ai}
        systemUsers={systemUsers}
        onSaved={() => { setEditing(false); onRefresh(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const handleStatus = async (status) => {
    if (status === 'Completed') {
      const taskCount = tasks.length;
      let subPending = 0;
      tasks.forEach(t => { subPending += (t.subtasks || []).filter(s => !s.isDone).length; });
      const incompleteTasks = tasks.filter(t => t.status !== 'Completed').length;
      if (incompleteTasks > 0 || subPending > 0) {
        const lines = [`Mark action item as Completed?`, ''];
        if (incompleteTasks > 0) lines.push(`• ${incompleteTasks} of ${taskCount} task(s) will be marked Completed`);
        if (subPending > 0) lines.push(`• ${subPending} pending subtask(s) will be ticked`);
        if (!window.confirm(lines.join('\n'))) return;
      }
    }
    try {
      const { data } = await axiosInstance.patch(`/api/vision/action-items/${ai._id}/status`, { status });
      onRollup?.(data.rollup);
      onRefresh();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update status');
    }
  };
  const handleDelete = async () => {
    if (!window.confirm(`Delete action item "${ai.title}" and all its tasks?`)) return;
    try {
      await axiosInstance.delete(`/api/vision/action-items/${ai._id}`);
      onRefresh();
    } catch (e) { console.error(e); }
  };
  const handleTaskDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axiosInstance.delete(`/api/vision/tasks/${id}`);
      onRefresh();
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{
      background: '#fff', border: '1px solid #ddd6fe', borderLeft: '4px solid #8b5cf6',
      borderRadius: 8, padding: 11, marginBottom: 10, boxShadow: '0 1px 2px rgba(91,33,182,.05)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setOpen(o => !o)} aria-label={`${open ? 'Collapse' : 'Expand'} action item ${ai.title}`} title={`${open ? 'Collapse' : 'Expand'} action item`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6d28d9', padding: 3, borderRadius: 4 }}>
          {open ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#6d28d9', letterSpacing: '.08em', marginBottom: 2 }}>ACTION ITEM</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{ai.title}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>{breadcrumb}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <Pill bg="#ede9fe" fg="#5b21b6">{ai.weight}% weight</Pill>
            {ai.deadline && <span style={{ fontSize: 10, color: '#94a3b8' }}>📅 due {new Date(ai.deadline).toLocaleDateString('en-GB')}</span>}
            {ai.completedAt
              ? <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>✓ done {new Date(ai.completedAt).toLocaleDateString('en-GB')}</span>
              : ai.updatedAt && <span style={{ fontSize: 10, color: '#94a3b8' }}>🕒 updated {new Date(ai.updatedAt).toLocaleDateString('en-GB')}</span>}
            {(ai.responsible || []).length > 0 && (
              <span style={{ fontSize: 10, color: '#94a3b8' }}>
                👤 {(ai.responsible || []).map(r => r.userId?.fullName || r.externalName).join(', ')}
              </span>
            )}
            {ai.priority && (
              <Pill
                bg={ai.priority === 'Urgent' ? '#fee2e2' : ai.priority === 'High' ? '#ffedd5' : '#dbeafe'}
                fg={ai.priority === 'Urgent' ? '#991b1b' : ai.priority === 'High' ? '#9a3412' : '#1e40af'}
              >{ai.priority}</Pill>
            )}
            {ai.autoRisk && ai.autoRisk !== 'Low' && <Pill bg="#fee2e2" fg="#991b1b">⚠ {ai.autoRisk} risk</Pill>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
            <ProgressBar pct={ai.completionPercentage} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', minWidth: 30, textAlign: 'right' }}>
              {ai.completionPercentage}%
            </span>
          </div>
        </div>
        <StatusSelect value={ai.status} onChange={handleStatus} />
        {canEdit && (
          <button onClick={() => setEditing(true)} title="Edit action item"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}>
            <HiOutlinePencil size={13} />
          </button>
        )}
        {canEdit && (
          <button onClick={handleDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
            <HiOutlineTrash size={13} />
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 11, paddingLeft: 22, borderLeft: '1px solid #ddd6fe', marginLeft: 7 }}>
          {ai.description && <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{ai.description}</div>}
          <AttachmentList
            entityType="action-items"
            entityId={ai._id}
            attachments={ai.attachments || []}
            canEdit={canEdit}
            onChange={() => onRefresh()}
            compact
          />
          <div style={{ fontSize: 9, fontWeight: 800, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6, marginTop: 11 }}>
            Tasks ({tasks.length})
          </div>
          {tasks.length === 0 && <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 6 }}>No tasks yet.</div>}
          {tasks.map(t => (
            <TaskNode key={t._id}
              task={t}
              onUpdate={() => onRefresh()}
              onDelete={handleTaskDelete}
              onRefresh={onRefresh}
              onRollup={onRollup}
              canEdit={canEdit}
              systemUsers={systemUsers}
            />
          ))}
          {showAddTask ? (
            <AddTaskInline actionItemId={ai._id} systemUsers={systemUsers}
              onCreated={() => { setShowAddTask(false); onRefresh(); }}
              onCancel={() => setShowAddTask(false)} />
          ) : (
            <button onClick={() => setShowAddTask(true)} className="v26-btn v26-btn--secondary v26-btn--sm" style={{ marginTop: 4, fontSize: 11 }}>
              <HiOutlinePlus size={11} /> Add task
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Add Action Item form
// ═══════════════════════════════════════════════════════════════════════════
const AddActionItemInline = ({ goalId, systemUsers, onCreated, onCancel }) => {
  const [form, setForm] = useState({
    title: '', description: '', deadline: '', priority: 'Medium', responsible: []
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.post('/api/vision/action-items', {
        goal: goalId, title: form.title.trim(),
        description: form.description, deadline: form.deadline || undefined,
        priority: form.priority, responsible: form.responsible
      });
      onCreated(data.data);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to add action item');
    } finally { setSaving(false); }
  };
  return (
    <div className="v26-roadmap-form" style={{ background: '#fff', borderRadius: 9, padding: 12, marginBottom: 10, border: '1px solid #ddd6fe', boxShadow: '0 4px 12px rgba(91,33,182,.06)' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="v26-roadmap-field" style={{ flex: 2, minWidth: 160 }}><span>Action item name</span><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Launch the marketing campaign" autoFocus /></label>
        <label className="v26-roadmap-field" style={{ flex: '1 1 125px' }}><span>Due date</span><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} /></label>
        <label className="v26-roadmap-field" style={{ flex: '1 1 110px' }}><span>Priority</span><select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
          {['Low','Medium','High','Urgent'].map(p => <option key={p}>{p}</option>)}
        </select></label>
      </div>
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 5 }}>Responsible</div>
        <AssigneePicker value={form.responsible} onChange={(arr) => setForm(f => ({ ...f, responsible: arr }))}
          systemUsers={systemUsers} compact placeholder="Select responsible people…" />
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
        <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
          {saving ? '…' : 'Add Action Item'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Edit Goal inline form
// ═══════════════════════════════════════════════════════════════════════════
const EditGoalInline = ({ goal, systemUsers, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    name: goal.name || '',
    description: goal.description || '',
    owner: goal.owner?._id || (typeof goal.owner === 'string' ? goal.owner : '') || '',
    deadline: goal.deadline ? new Date(goal.deadline).toISOString().slice(0, 10) : ''
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/vision/goals/${goal._id}`, {
        name: form.name.trim(),
        description: form.description,
        owner: form.owner || null,
        deadline: form.deadline || null
      });
      onSaved();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update goal');
    } finally { setSaving(false); }
  };
  return (
    <div className="v26-roadmap-form" style={{ background: '#fff', border: '1px dashed #a5b4fc', borderRadius: 10, padding: 12, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>EDIT GOAL</div>
      <div className="v26-roadmap-goal-fields" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
        <label className="v26-roadmap-field"><span>Goal name</span><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Improve customer retention" autoFocus /></label>
        <label className="v26-roadmap-field"><span>Owner</span><select value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}>
          <option value="">Owner…</option>
          {systemUsers.map(u => <option key={u._id} value={u._id}>{u.fullName || u.username}</option>)}
        </select></label>
        <label className="v26-roadmap-field"><span>Due date</span><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} /></label>
      </div>
      <label className="v26-roadmap-field" style={{ marginTop: 10 }}><span>Description <em>Optional</em></span><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="Add context or the outcome this goal should achieve" rows={2} /></label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Goal node
// ═══════════════════════════════════════════════════════════════════════════
const GoalNode = ({ goal, systemUsers, breadcrumb, onRefresh, onRollup, canEdit }) => {
  const [open, setOpen] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <EditGoalInline
        goal={goal}
        systemUsers={systemUsers}
        onSaved={() => { setEditing(false); onRefresh(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete goal "${goal.name}" and all its action items + tasks?`)) return;
    try {
      await axiosInstance.delete(`/api/vision/goals/${goal._id}`);
      onRefresh();
    } catch (e) { console.error(e); alert(e.response?.data?.message || 'Failed'); }
  };

  return (
    <div style={{ marginBottom: 18, background: '#f8faff', border: '1px solid #c7d2fe', borderLeft: '4px solid #6366f1', borderRadius: 10, padding: 14, boxShadow: '0 2px 5px rgba(79,70,229,.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => setOpen(o => !o)} aria-label={`${open ? 'Collapse' : 'Expand'} goal ${goal.name}`} title={`${open ? 'Collapse' : 'Expand'} goal`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4338ca', padding: 3, borderRadius: 4 }}>
          {open ? <HiOutlineChevronDown size={16} /> : <HiOutlineChevronRight size={16} />}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#4338ca', letterSpacing: '.09em', marginBottom: 3 }}>GOAL</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{goal.name}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>{breadcrumb}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <Pill bg="#ede9fe" fg="#5b21b6">{goal.weight}% weight</Pill>
            {goal.owner && <Pill bg="#dbeafe" fg="#1e40af">Owner: {goal.owner.fullName || goal.owner.username}</Pill>}
            {goal.deadline && <span style={{ fontSize: 11, color: '#64748b' }}>📅 {new Date(goal.deadline).toLocaleDateString('en-GB')}</span>}
            {goal.autoRisk && goal.autoRisk !== 'Low' && <Pill bg="#fee2e2" fg="#991b1b">⚠ {goal.autoRisk} risk</Pill>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <ProgressBar pct={goal.completionPercentage} />
            <span style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', minWidth: 38, textAlign: 'right' }}>
              {goal.completionPercentage}%
            </span>
          </div>
        </div>
        <Pill bg={(statusBg[goal.status] || {}).bg} fg={(statusBg[goal.status] || {}).fg}>{goal.status}</Pill>
        {canEdit && (
          <button onClick={() => setEditing(true)} className="v26-btn v26-btn--secondary v26-btn--sm" title="Edit goal">
            <HiOutlinePencil size={11} />
          </button>
        )}
        {canEdit && (
          <button onClick={handleDelete} className="v26-btn v26-btn--danger v26-btn--sm" title="Delete goal">
            <HiOutlineTrash size={11} />
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 13, paddingLeft: 28, borderLeft: '1px solid #c7d2fe', marginLeft: 8 }}>
          {goal.description && <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic', marginBottom: 8 }}>{goal.description}</div>}
          <AttachmentList
            entityType="goals"
            entityId={goal._id}
            attachments={goal.attachments || []}
            canEdit={canEdit}
            onChange={() => onRefresh()}
            compact
          />
          <div style={{ fontSize: 9, fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 7, marginTop: 12 }}>
            Action Items ({(goal.actionItems || []).length})
          </div>
          {(goal.actionItems || []).length === 0 && <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 6 }}>No action items yet — add one to start.</div>}
          {(goal.actionItems || []).map(ai => (
            <ActionItemNode key={ai._id} ai={ai}
              systemUsers={systemUsers}
              breadcrumb={`${breadcrumb} › ${ai.title}`}
              onRefresh={onRefresh}
              onRollup={onRollup}
              canEdit={canEdit} />
          ))}
          {showAdd ? (
            <AddActionItemInline goalId={goal._id} systemUsers={systemUsers}
              onCreated={() => { setShowAdd(false); onRefresh(); }}
              onCancel={() => setShowAdd(false)} />
          ) : (
            <button onClick={() => setShowAdd(true)} className="v26-btn v26-btn--secondary v26-btn--sm" style={{ fontSize: 11 }}>
              <HiOutlinePlus size={12} /> Add Action Item
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Add Goal form
// ═══════════════════════════════════════════════════════════════════════════
const AddGoalInline = ({ missionId, systemUsers, onCreated, onCancel }) => {
  const [form, setForm] = useState({ name: '', description: '', owner: '', deadline: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.post('/api/vision/goals', {
        mission: missionId, name: form.name.trim(),
        description: form.description, owner: form.owner || undefined,
        deadline: form.deadline || undefined
      });
      onCreated(data.data);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to add goal');
    } finally { setSaving(false); }
  };
  return (
    <div className="v26-roadmap-form" style={{ background: '#fff', border: '1px dashed #a5b4fc', borderRadius: 10, padding: 12, marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#4338ca', marginBottom: 10, letterSpacing: '.07em' }}>NEW GOAL</div>
      <div className="v26-roadmap-goal-fields" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, overflow: 'hidden' }}>
        <label className="v26-roadmap-field"><span>Goal name</span><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Improve customer retention" autoFocus /></label>
        <label className="v26-roadmap-field"><span>Owner</span><select value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}>
          <option value="">Owner…</option>
          {systemUsers.map(u => <option key={u._id} value={u._id}>{u.fullName || u.username}</option>)}
        </select></label>
        <label className="v26-roadmap-field"><span>Due date</span><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} /></label>
      </div>
      <label className="v26-roadmap-field" style={{ marginTop: 10 }}><span>Description <em>Optional</em></span><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="Add context or the outcome this goal should achieve" rows={2} /></label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
          {saving ? '…' : 'Add Goal'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Main RoadmapDrawer
// ═══════════════════════════════════════════════════════════════════════════
const RoadmapDrawer = ({ missionId, systemUsers = [], onClose, onAnyChange, onRollup, refreshKey }) => {
  const { user } = useAuth();
  const mayEdit = user?.role === 'system_admin' || hasCardAction(user?.permissions, 'vision', 'edit');
  const mayCreate = user?.role === 'system_admin' || hasCardAction(user?.permissions, 'vision', 'create');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const hasLoaded = useRef(false);

  const load = useCallback(async () => {
    // Keep the current tree mounted during refreshes so edits do not close the
    // drawer or reset expanded nodes.
    if (!hasLoaded.current) setLoading(true);
    try {
      const { data: resp } = await axiosInstance.get(`/api/vision/missions/${missionId}/roadmap`);
      setData(resp.data);
      hasLoaded.current = true;
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [missionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const refresh = () => { load(); onAnyChange?.(); };

  if (loading) return (
    <div className="v26-drawer-overlay" onClick={onClose}>
      <div className="v26-drawer v26-drawer--roadmap" onClick={e => e.stopPropagation()}>
        <div className="v26-drawer-body">
          <div className="v26-empty">Loading roadmap…</div>
        </div>
      </div>
    </div>
  );

  if (!data) return null;
  const { vision, mission, goals } = data;
  const missionBreadcrumb = `${vision?.name || vision?.year} › ${mission.name}`;
  // A locked mission is a closed phase: the whole tree is read-only, for admins too.
  const locked = !!mission.isLocked;
  const canEdit = mayEdit && !locked;
  const canCreate = mayCreate && !locked;

  return (
    <div className="v26-drawer-overlay" onClick={onClose}>
      <div className="v26-drawer v26-drawer--roadmap" onClick={e => e.stopPropagation()}>
        <div className="v26-drawer-header" style={{ position: 'relative' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
            ROADMAP
          </div>
          <h2 style={{ margin: 0 }}>{mission.name}</h2>
          <p style={{ marginTop: 4 }}>
            {vision?.name || `Vision ${vision?.year}`} ·{' '}
            {goals.length} goal{goals.length !== 1 ? 's' : ''} ·{' '}
            <strong>{mission.completionPercentage}%</strong> complete
          </p>
          <button className="v26-drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="v26-drawer-body" style={{ background: '#fafafa' }}>
          {locked && (
            <div style={{
              background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 10,
              padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#334155'
            }}>
              <strong>🔒 This mission is locked.</strong> It is a closed phase — nothing inside it can be
              changed, and it is hidden from all non-admin users. A system admin can unlock it from
              Mission Control.
              {mission.lockReason && <div style={{ marginTop: 4, fontStyle: 'italic' }}>{mission.lockReason}</div>}
            </div>
          )}

          {/* Mission summary bar */}
          <div style={{ background: '#fff', borderRadius: 10, padding: 14, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Mission Progress
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  <ProgressBar pct={mission.completionPercentage} />
                  <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{mission.completionPercentage}%</span>
                </div>
              </div>
              <Pill bg={(statusBg[mission.status] || {}).bg} fg={(statusBg[mission.status] || {}).fg} style={{ fontSize: 11 }}>
                {mission.status}
              </Pill>
              {mission.autoRisk && mission.autoRisk !== 'Low' && (
                <Pill bg="#fee2e2" fg="#991b1b" style={{ fontSize: 11 }}>⚠ {mission.autoRisk} risk</Pill>
              )}
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
              {mission.head && <span>Head: <strong>{mission.head.fullName}</strong></span>}
              {(mission.team || []).length > 0 && (
                <span>Team: <strong>{mission.team.map(t => t.fullName).join(', ')}</strong></span>
              )}
              <span>Target: <strong>{new Date(mission.targetDate).toLocaleDateString('en-GB')}</strong></span>
            </div>

            <AttachmentList
              entityType="missions"
              entityId={mission._id}
              attachments={mission.attachments || []}
              canEdit={canEdit}
              onChange={() => refresh()}
            />
          </div>

          {/* Goals */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Goals</div>
            {canCreate && (
              <button onClick={() => setShowAddGoal(s => !s)} className="v26-btn v26-btn--primary v26-btn--sm">
                <HiOutlinePlus size={13} /> {showAddGoal ? 'Cancel' : 'Add Goal'}
              </button>
            )}
          </div>

          {showAddGoal && canCreate && (
            <AddGoalInline missionId={mission._id} systemUsers={systemUsers}
              onCreated={() => { setShowAddGoal(false); refresh(); }}
              onCancel={() => setShowAddGoal(false)} />
          )}

          {goals.length === 0 && !showAddGoal && (
            <div className="v26-empty">
              No goals yet for this mission.<br />
              {canCreate && <span style={{ fontSize: 13 }}>Click <strong>Add Goal</strong> above to start building the roadmap.</span>}
            </div>
          )}

          {goals.map(g => (
            <GoalNode key={g._id} goal={g}
              systemUsers={systemUsers}
              breadcrumb={`${missionBreadcrumb} › ${g.name}`}
              onRefresh={refresh}
              onRollup={onRollup}
              canEdit={canEdit} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoadmapDrawer;
